import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  BarChart,
  Store,
  Package,
  ShoppingCart,
  Megaphone,
  CreditCard,
  Receipt,
  Users,
  UserCheck,
  Settings,
  Folder,
  Shield,
  Image as ImageIcon,
  X,
  LogsIcon,
  Send,
  Flag,
  Radio,
  Layers,
  Gift,
  Share2,
  Wallet,
  Truck,
} from "lucide-react";
import SidebarItem from "./SidebarItem";
import logo from "@/assets/logo.jpg";
import {
  BuildingOfficeIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../features/users/context/AuthContext";
import { useReportsUnreadCount } from "../../features/reports/hooks/useReportsUnreadCount";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
}) => {
  const authContext = useAuth();
  const user = authContext?.user;
  const reportsUnreadCount = useReportsUnreadCount();

  // Accordéon : un seul dropdown ouvert à la fois (clé = texte de l'onglet).
  const [openMenu, setOpenMenu] = React.useState(null);
  const toggleMenu = (key) =>
    setOpenMenu((prev) => (prev === key ? null : key));

  const navigationItems = [
    { icon: BarChart, text: "Tableau de bord", active: true, to: "/dashboard" },
    {
      icon: Store,
      text: "Marketplace",
      to: "#",
      subItems: [
        { text: "Boutiques", to: "/shops" },
        { text: "Articles", to: "/all/products" },
        { text: "Produits recherchés", to: "/product-requests" },
        { text: "Commandes", to: "/orders" },
        { text: "Abonnements boutique", to: "/shops/partnerships" },
        { text: "Catégories d'articles", to: "/categories" },
      ],
    },
    { icon: Truck, text: "Livreurs", to: "/deliveries" },
    {
      icon: BuildingOfficeIcon,
      text: "Mise en relation",
      to: "#",
      subItems: [
        { text: "Services", to: "/services" },
        { text: "Catégories de services", to: "/services/categories" },
        { text: "Abonnements services", to: "/services/subscriptions" },
      ],
    },
    {
      icon: Share2,
      text: "Réseaux",
      to: "#",
      badge: reportsUnreadCount,
      subItems: [
        { text: "Lives", to: "/lives" },
        { text: "Stories", to: "/stories" },
        { text: "Signalements", to: "/reports" },
      ],
    },
    {
      icon: Megaphone,
      text: "Marketing",
      to: "#",
      subItems: [
        { text: "Publicités", to: "/advertisements" },
        { text: "Campagnes", to: "/marketing" },
        { text: "Points de fidélité", to: "/loyalty" },
      ],
    },
  ];

  const paiementItems = [
    { icon: CreditCard, text: "Paiements", to: "/paiements" },
    { icon: Receipt, text: "Transactions", to: "/stats/paiements" },
    { icon: Wallet, text: "Retraits", to: "/withdrawals" },
    { icon: Layers, text: "Plans", to: "/plans" },
  ];

  const adminItems = [
    {
      icon: Users,
      text: "Utilisateurs",
      to: "#",
      subItems: [
        { text: "Utilisateurs", to: "/users" },
        ...(user?.is_superadmin
          ? [{ text: "Administrateurs", to: "/admins" }]
          : []),
      ],
    },
    {
      icon: Settings,
      text: "Paramètres",
      to: "#",
      subItems: [
        { icon: Shield, text: "Systèmes", to: "/settings" },
        {
          icon: DevicePhoneMobileIcon,
          text: "Version mobile",
          to: "/app-versions",
        },
        { icon: LogsIcon, text: "Logs", to: "/logs" },
      ],
    },
  ];

  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 flex flex-col h-screen bg-white shadow-lg transform transition-all duration-300 ease-in-out ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      } ${sidebarCollapsed ? "w-20" : "w-64"} lg:relative lg:translate-x-0`}
    >
      <div className="flex items-center justify-center h-20 p-4 border-b bg-primary shrink-0">
        <div className="flex items-center truncate ">
          <img
            src={logo}
            alt="logo"
            className={`transition-all duration-300 ${
              sidebarCollapsed ? "w-8 md:w-16 lg:w-24" : "w-10 md:w-24 lg:w-24"
            }`}
          />
        </div>
        <div className="flex">
          {!sidebarCollapsed && (
            <button
              className="lg:hidden p-1"
              onClick={() => setSidebarOpen(false)}
            >
              <X className=" ms-10 w-8 h-8 text-white text-lg" />
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-end px-3 py-2 shrink-0">
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-1 rounded-md hover:bg-gray-100 text-gray-500"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      <nav
        className={`px-3 py-2 flex-1 overflow-y-auto min-h-0 ${
          sidebarCollapsed ? "flex flex-col items-center" : ""
        }`}
      >
        <div className="space-y-1 w-full">
          {navigationItems.map((item, index) => (
            <SidebarItem
              key={index}
              icon={item.icon}
              text={item.text}
              active={item.active}
              collapsed={sidebarCollapsed}
              to={item.to}
              subItems={item.subItems}
              badgeCount={item.badge ?? 0}
              isOpen={openMenu === item.text}
              onToggle={item.subItems ? () => toggleMenu(item.text) : undefined}
            />
          ))}
        </div>
        <div className="pt-6 mt-6 border-t border-gray-200">
          <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Transactions
          </h3>
          <div className="space-y-1 w-full">
            {paiementItems.map((item, index) => (
              <SidebarItem
                key={index}
                icon={item.icon}
                text={item.text}
                active={item.active}
                collapsed={sidebarCollapsed}
                to={item.to}
              />
            ))}
          </div>
        </div>

        {!sidebarCollapsed ? (
          <div className="pt-6 mt-6 border-t border-gray-200">
            <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Administration
            </h3>
            <div className="mt-2 space-y-1">
              {adminItems.map((item, index) => (
                <SidebarItem
                  key={index}
                  icon={item.icon}
                  text={item.text}
                  collapsed={sidebarCollapsed}
                  subItems={item.subItems}
                  to={item.to}
                  isOpen={openMenu === item.text}
                  onToggle={item.subItems ? () => toggleMenu(item.text) : undefined}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="pt-6 mt-6 border-t border-gray-200 flex flex-col items-center w-full">
            <div className="mt-2 space-y-3 w-full">
              {adminItems.map((item, index) => (
                <SidebarItem
                  key={index}
                  icon={item.icon}
                  text={item.text}
                  collapsed={sidebarCollapsed}
                />
              ))}
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Sidebar;
